/**
 * Ava & Bear "Build your bundle" page (sections/ab-bundle-builder.liquid).
 *
 * Liquid renders the print swatches and one card per piece (pod, nursing cushion, changing mat,
 * nappy clutch), plus the option data as JSON: every complete product of each piece with its
 * print, variant, price and stock status. Choosing a print sets every piece to it (or to its
 * closest match when the piece isn't made in that print); a swatch on a card mixes in another
 * print for that piece only. The summary shows a flat-lay of the chosen pieces, the saving tier
 * and the total. "Add bundle to cart" posts all chosen variants in one /cart/add.js request with a
 * shared `_bundle` id, saves the optional gift note as cart attributes, and announces the change
 * with the theme's CartLinesUpdateEvent so the cart drawer and icon refresh.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} PieceOption
 * @property {string} print
 * @property {number} productId
 * @property {number} variantId
 * @property {number} price
 * @property {'in_stock' | 'preorder' | 'sold_out'} status
 * @property {string} url
 * @property {string | null} image
 * @property {string} alt
 */

/** @typedef {{title: string, moment: string, options: PieceOption[]}} Piece */
/** @typedef {{on: boolean, option: PieceOption | null}} PieceState */

const MESSAGE_DURATION = 8000;
const ORDER = ['pod', 'cushion', 'mat', 'clutch'];

/** @param {string} value */
const norm = (value) => value.trim().toLowerCase();

class AbBundleBuilder extends HTMLElement {
  /** @type {Record<string, Piece>} */
  pieces = {};
  /** @type {Record<string, string>} */
  fallbacks = {};
  /** @type {Array<{min: number, pct: number}>} */
  tiers = [];
  /** @type {Record<string, string>} */
  labels = {};
  /** @type {Record<string, PieceState>} */
  state = {};
  base = '';
  #busy = false;
  /** @type {number | undefined} */
  #messageTimer;
  /** Short confirmation shown in the bottom bar in place of the piece count. */
  #barNote = '';
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #builderInView = false;
  #summaryInView = false;

  connectedCallback() {
    try {
      const data = JSON.parse(this.querySelector('[data-abb-data]')?.textContent || '{}');
      this.pieces = data.pieces || {};
      this.tiers = (data.tiers || []).filter((/** @type {{min: number}} */ t) => t.min > 0);
      for (const [from, to] of Object.entries(data.fallbacks || {})) this.fallbacks[norm(from)] = to;
      this.labels = JSON.parse(this.querySelector('[data-abb-labels]')?.textContent || '{}');
    } catch (error) {
      console.warn('[ab-bundle-builder] Could not read bundle data', error);
      return;
    }

    // Start on the first print that every piece is made in, else the first print shown.
    const swatches = this.#swatches();
    const full = swatches.find((s) => ORDER.every((k) => this.#option(k, s.dataset.print || '')));
    this.#setBase((full || swatches[0])?.dataset.print || '', true);

    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-abb-families]')?.addEventListener('keydown', this.#onSwatchKey);
    this.querySelector('[data-abb-gift-on]')?.addEventListener('change', this.#renderGift);
    this.querySelectorAll('[data-abb-gift]').forEach((el) => el.addEventListener('input', this.#renderGift));
    this.#renderGift();
    this.#watchBar();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#messageTimer);
    this.#observer?.disconnect();
  }

  /**
   * The bottom bar shows while the builder is on screen and the full summary is not
   * (CSS keeps it to phones and tablets, where the summary sits below the pieces).
   */
  #watchBar() {
    const builder = this.querySelector('#abb-builder');
    const summary = this.querySelector('.abb-summary');
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-bar]'));
    if (!builder || !summary || !bar || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === builder) this.#builderInView = entry.isIntersecting;
        if (entry.target === summary) this.#summaryInView = entry.isIntersecting;
      }
      const show = this.#builderInView && !this.#summaryInView;
      bar.hidden = !show;
      this.classList.toggle('has-bar', show);
    });
    this.#observer.observe(builder);
    this.#observer.observe(summary);
  }

  #swatches() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-abb-families] [data-print]')));
  }

  /**
   * @param {string} key
   * @param {string} print
   * @returns {PieceOption | undefined}
   */
  #option(key, print) {
    return this.pieces[key]?.options.find((o) => norm(o.print) === norm(print) && o.status !== 'sold_out');
  }

  /**
   * The option a piece takes for a print: that print, else its closest match, else the first available.
   * @param {string} key
   * @param {string} print
   */
  #resolve(key, print) {
    const fallback = this.fallbacks[norm(print)];
    return (
      this.#option(key, print) ||
      (fallback && this.#option(key, fallback)) ||
      this.pieces[key]?.options.find((o) => o.status !== 'sold_out') ||
      null
    );
  }

  /**
   * @param {string} print
   * @param {boolean} [initial]
   */
  #setBase(print, initial = false) {
    this.base = print;
    for (const key of ORDER) {
      const option = this.#resolve(key, print);
      const on = initial ? Boolean(option) : (this.state[key]?.on ?? true);
      this.state[key] = { on: on && Boolean(option), option };
    }
    this.#render();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;

    const swatch = target.closest('[data-abb-families] [data-print]');
    if (swatch instanceof HTMLElement) {
      this.#setBase(swatch.dataset.print || '');
      return;
    }

    const look = target.closest('[data-abb-look]');
    if (look instanceof HTMLElement) {
      this.#applyLook(look.dataset.abbLook || '');
      return;
    }

    if (target.closest('[data-abb-add]')) {
      this.#onAdd();
      return;
    }

    const card = target.closest('[data-abb-piece]');
    if (!(card instanceof HTMLElement)) return;
    const key = card.dataset.abbPiece || '';
    const piece = this.state[key];
    if (!piece) return;

    const pick = target.closest('[data-abb-pick]');
    if (pick instanceof HTMLElement) {
      const option = this.#option(key, pick.dataset.abbPick || '');
      if (option) this.state[key] = { on: true, option };
    } else if (target.closest('[data-abb-back]')) {
      this.state[key] = { on: true, option: this.#resolve(key, this.base) };
    } else if (target.closest('[data-abb-toggle], [data-abb-remove]')) {
      if (piece.option) piece.on = !piece.on;
    } else {
      return;
    }
    this.#hideMessage();
    this.#render();
  };

  /** @param {KeyboardEvent} event */
  #onSwatchKey = (event) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const swatches = this.#swatches();
    const index = swatches.findIndex((s) => norm(s.dataset.print || '') === norm(this.base));
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = swatches[(index + step + swatches.length) % swatches.length];
    if (!next) return;
    this.#setBase(next.dataset.print || '');
    next.focus();
  };

  /** @param {string} spec "pod:123,cushion:456," */
  #applyLook(spec) {
    /** @type {Record<string, number>} */
    const chosen = {};
    for (const pair of spec.split(',')) {
      const [key, id] = pair.split(':');
      if (key && id) chosen[key] = Number(id);
    }
    for (const key of ORDER) {
      const option = this.pieces[key]?.options.find((o) => o.productId === chosen[key] && o.status !== 'sold_out');
      this.state[key] = option ? { on: true, option } : { on: false, option: this.#resolve(key, this.base) };
    }
    const lead = this.state.pod?.option || ORDER.map((k) => this.state[k]?.option).find(Boolean);
    if (lead) this.base = lead.print;
    this.#hideMessage();
    this.#render();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelector('#abb-builder')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'ZAR');
  }

  /**
   * @param {string} text
   * @param {Record<string, string | number>} values
   */
  #fill(text, values) {
    return Object.entries(values).reduce((out, [k, v]) => out.split(`[${k}]`).join(String(v)), text || '');
  }

  /** @param {number} count */
  #rate(count) {
    let pct = 0;
    for (const tier of this.tiers) if (count >= tier.min) pct = tier.pct;
    return pct;
  }

  #render() {
    this.#renderSwatches();
    this.#renderPieces();
    this.#renderSummary();
  }

  #renderSwatches() {
    for (const swatch of this.#swatches()) {
      const on = norm(swatch.dataset.print || '') === norm(this.base);
      swatch.setAttribute('aria-checked', String(on));
      swatch.tabIndex = on ? 0 : -1;
    }
  }

  #renderPieces() {
    const labels = this.labels;
    for (const card of /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-abb-piece]')))) {
      const key = card.dataset.abbPiece || '';
      const piece = this.state[key];
      const option = piece?.option;
      card.classList.toggle('is-on', Boolean(piece?.on));
      card.classList.toggle('is-unavailable', !option);

      const img = /** @type {HTMLImageElement | null} */ (card.querySelector('[data-abb-piece-img]'));
      if (img) {
        if (option?.image) {
          if (img.getAttribute('src') !== option.image) img.src = option.image;
          img.alt = option.alt;
          img.hidden = false;
        } else {
          img.hidden = true;
        }
      }

      const tag = /** @type {HTMLElement | null} */ (card.querySelector('[data-abb-piece-tag]'));
      if (tag) {
        tag.hidden = option?.status !== 'preorder';
        tag.textContent = this.dataset.labelPreorder || 'Pre-order';
      }

      const link = /** @type {HTMLAnchorElement | null} */ (card.querySelector('[data-abb-piece-link]'));
      if (link && option) link.href = option.url;

      const price = card.querySelector('[data-abb-piece-price]');
      if (price) price.textContent = option ? this.#money(option.price) : this.dataset.labelSoldOut || '';

      const printName = card.querySelector('[data-abb-piece-print]');
      if (printName) printName.textContent = option?.print || '';

      const madeInBase = Boolean(this.#option(key, this.base));
      const mixed = Boolean(option) && madeInBase && norm(option?.print || '') !== norm(this.base);
      const subbed = Boolean(option) && !madeInBase && norm(option?.print || '') === norm(this.#resolve(key, this.base)?.print || '');

      const note = /** @type {HTMLElement | null} */ (card.querySelector('[data-abb-piece-note]'));
      if (note) {
        note.hidden = !subbed;
        note.textContent = subbed ? this.#fill(labels.notMade, { print: this.base, match: option?.print || '' }) : '';
      }

      const stateTag = /** @type {HTMLElement | null} */ (card.querySelector('[data-abb-piece-state]'));
      if (stateTag) {
        const isMixed = mixed || (Boolean(option) && !madeInBase && !subbed);
        stateTag.textContent = isMixed ? labels.mixed : labels.matches;
        stateTag.classList.toggle('is-mixed', isMixed);
      }

      for (const pick of /** @type {HTMLElement[]} */ (Array.from(card.querySelectorAll('[data-abb-pick]')))) {
        const print = pick.dataset.abbPick || '';
        const available = Boolean(this.#option(key, print));
        pick.setAttribute('aria-pressed', String(norm(print) === norm(option?.print || '')));
        pick.toggleAttribute('disabled', !available);
      }

      const back = /** @type {HTMLElement | null} */ (card.querySelector('[data-abb-back]'));
      if (back) {
        back.hidden = !mixed;
        back.textContent = this.#fill(labels.back, { print: this.base });
      }

      const remove = /** @type {HTMLElement | null} */ (card.querySelector('[data-abb-remove]'));
      if (remove) remove.hidden = !piece?.on;

      const toggle = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-abb-toggle]'));
      if (toggle) {
        const title = this.pieces[key]?.title || '';
        toggle.textContent = piece?.on ? labels.added : labels.add;
        toggle.setAttribute('aria-pressed', String(Boolean(piece?.on)));
        toggle.setAttribute('aria-label', `${piece?.on ? labels.remove || 'Remove' : labels.add} ${title}`.trim());
        toggle.classList.toggle('abb-btn--primary', Boolean(piece?.on));
        toggle.classList.toggle('abb-btn--ghost', !piece?.on);
        toggle.disabled = !option;
      }
    }
  }

  #chosen() {
    return ORDER.filter((k) => this.state[k]?.on && this.state[k]?.option).map((k) => ({
      key: k,
      option: /** @type {PieceOption} */ (this.state[k]?.option),
    }));
  }

  #renderSummary() {
    const labels = this.labels;
    const chosen = this.#chosen();
    const count = chosen.length;
    const pct = this.#rate(count);
    const subtotal = chosen.reduce((sum, c) => sum + Number(c.option.price), 0);
    const saving = Math.round((subtotal * pct) / 100);
    const total = subtotal - saving;

    const countEl = this.querySelector('[data-abb-count]');
    if (countEl) countEl.textContent = count === 1 ? labels.piece : this.#fill(labels.pieces, { n: count });

    for (const img of /** @type {HTMLImageElement[]} */ (Array.from(this.querySelectorAll('[data-abb-fl]')))) {
      const state = this.state[img.dataset.abbFl || ''];
      const option = state?.option;
      if (option?.image && img.getAttribute('src') !== option.image) img.src = option.image;
      img.hidden = !option?.image;
      img.classList.toggle('is-off', !state?.on);
    }
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-fl-empty]'));
    if (empty) empty.hidden = count > 0;

    const lines = this.querySelector('[data-abb-lines]');
    if (lines) {
      lines.replaceChildren(
        ...chosen.map(({ key, option }) => {
          const li = document.createElement('li');
          const name = document.createElement('span');
          name.textContent = this.pieces[key]?.title || '';
          const print = document.createElement('small');
          print.textContent = option.status === 'preorder' ? `${option.print} · ${this.dataset.labelPreorder}` : option.print;
          name.append(print);
          const amount = document.createElement('span');
          amount.className = 'abb-lines__amt';
          amount.textContent = this.#money(option.price);
          li.append(name, amount);
          return li;
        })
      );
    }

    // Progress: each tier label ends at an equal step along the track, the top tier at the end.
    const steps = this.tiers.length || 1;
    const reached = this.tiers.filter((t) => count >= t.min).length;
    const next = this.tiers[reached];
    const prevMin = this.tiers[reached - 1]?.min || 0;
    let fill = (reached / steps) * 100;
    if (next && count > prevMin) fill += ((count - prevMin) / (next.min - prevMin) / steps) * 100;
    const fillEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-fill]'));
    if (fillEl) fillEl.style.width = `${Math.min(fill, 100)}%`;
    this.querySelectorAll('[data-abb-tier]').forEach((el, i) => {
      el.classList.toggle('is-hit', count >= (this.tiers[i]?.min ?? Infinity));
    });

    const hint = this.querySelector('[data-abb-hint]');
    if (hint) {
      if (!next) hint.textContent = this.#fill(labels.hintDone, { pct });
      else if (next.min - count === 1 && next === this.tiers[this.tiers.length - 1] && count > 0)
        hint.textContent = this.#fill(labels.hintTier2, { pct: next.pct });
      else hint.textContent = this.#fill(labels.hintTier1, { n: next.min - count, pct: next.pct });
    }

    const totals = this.querySelector('[data-abb-totals]');
    if (totals) {
      /** @type {Array<[string, string, string?]>} */
      const rows = [[labels.subtotal, this.#money(subtotal)]];
      if (pct) rows.push([this.#fill(labels.saving, { pct }), `- ${this.#money(saving)}`, 'is-saving']);
      if (this.#giftOn()) rows.push([labels.gift, '✓']);
      rows.push([labels.total, this.#money(total), 'is-total']);
      totals.replaceChildren(
        ...rows.map(([label, value, cls]) => {
          const row = document.createElement('div');
          if (cls) row.className = cls;
          const a = document.createElement('span');
          a.textContent = label;
          const b = document.createElement('span');
          b.textContent = value;
          row.append(a, b);
          return row;
        })
      );
    }

    const lead = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-lead]'));
    if (lead) {
      const preorder = chosen.some((c) => c.option.status === 'preorder');
      lead.hidden = count === 0;
      lead.textContent = (preorder ? this.dataset.timingPreorder : this.dataset.timingStandard) || '';
    }

    if (!this.#busy) {
      for (const add of /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-abb-add]')))) {
        const inBar = Boolean(add.closest('[data-abb-bar]'));
        add.disabled = count === 0;
        if (!inBar) add.textContent = count ? `${labels.cart} · ${this.#money(total)}` : labels.cartEmpty;
      }
    }

    const barMeta = this.querySelector('[data-abb-bar-meta]');
    if (barMeta) {
      const pieces = count === 1 ? labels.piece : this.#fill(labels.pieces, { n: count });
      barMeta.textContent = this.#barNote || (count === 0 ? labels.cartEmpty : pct ? `${pieces} · ${pct}% off` : pieces);
    }
    const barTotal = this.querySelector('[data-abb-bar-total]');
    if (barTotal) barTotal.textContent = this.#money(total);
  }

  #giftOn() {
    const box = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-abb-gift-on]'));
    return Boolean(box?.checked);
  }

  /** @param {string} name */
  #giftValue(name) {
    const field = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (this.querySelector(`[data-abb-gift="${name}"]`));
    return field?.value.trim() || '';
  }

  #renderGift = () => {
    const fields = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-gift-fields]'));
    if (fields) fields.hidden = !this.#giftOn();
    const to = this.querySelector('[data-abb-preview="to"]');
    const msg = this.querySelector('[data-abb-preview="msg"]');
    const from = this.querySelector('[data-abb-preview="from"]');
    if (to) to.textContent = this.#giftValue('to') ? `${this.labels.giftTo} ${this.#giftValue('to')}` : '';
    if (msg) msg.textContent = this.#giftValue('msg');
    if (from) from.textContent = this.#giftValue('from') ? `${this.labels.giftFrom} ${this.#giftValue('from')}` : '';
    this.#renderSummary();
  };

  /**
   * @param {string} text
   * @param {'success' | 'error'} type
   */
  #showMessage(text, type) {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-message]'));
    if (!message) return;
    clearTimeout(this.#messageTimer);
    message.textContent = text;
    message.dataset.type = type;
    message.setAttribute('role', type === 'error' ? 'alert' : 'status');
    message.hidden = false;
    this.#barNote = text;
    if (type === 'success') this.#messageTimer = window.setTimeout(() => this.#hideMessage(), MESSAGE_DURATION);
  }

  #hideMessage() {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-abb-message]'));
    if (!message) return;
    message.hidden = true;
    message.textContent = '';
    if (this.#barNote) {
      this.#barNote = '';
      this.#renderSummary();
    }
  }

  #bundleId() {
    const random =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().split('-')[0]
        : Math.random().toString(36).slice(2, 10);
    return `AB-${Date.now().toString(36)}-${random}`.toUpperCase();
  }

  #routes() {
    // @ts-ignore Theme is a global set in layout/theme.liquid
    const routes = (window.Theme && window.Theme.routes) || {};
    /** @param {string | undefined} url @param {string} fallback */
    const js = (url, fallback) => {
      const base = url || fallback;
      return base.endsWith('.js') ? base : `${base}.js`;
    };
    return {
      add: js(routes.cart_add_url, '/cart/add'),
      cart: js(routes.cart_url, '/cart'),
      update: js(routes.cart_update_url, '/cart/update'),
    };
  }

  async #fetchCart() {
    const response = await fetch(this.#routes().cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {Record<string, unknown>} body */
  async #updateCart(body) {
    const response = await fetch(this.#routes().update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to update cart: ${response.status}`);
    return response.json();
  }

  /**
   * Removes any lines of this bundle that made it into the cart before an error,
   * so a failed add never leaves a partial bundle behind.
   * @param {string} bundleId
   */
  async #removePartial(bundleId) {
    try {
      const cart = await this.#fetchCart();
      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of cart.items || []) {
        if (item.properties && item.properties._bundle === bundleId) updates[item.key] = 0;
      }
      if (Object.keys(updates).length) await this.#updateCart({ updates });
    } catch (error) {
      console.warn('[ab-bundle-builder] Could not clean up a partial bundle', error);
    }
  }

  async #onAdd() {
    const buttons = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-abb-add]')));
    const chosen = this.#chosen();
    if (this.#busy || chosen.length === 0) return;

    this.#busy = true;
    this.#hideMessage();
    for (const add of buttons) {
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
    }

    const bundleId = this.#bundleId();
    const bundleName = this.dataset.bundleName || 'Build your bundle';
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const payload = {
      items: chosen.map(({ option }) => ({
        id: Number(option.variantId),
        quantity: 1,
        properties: { _bundle: bundleId, _bundle_name: bundleName },
      })),
      ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
    };

    let deferred = null;
    try {
      const response = await fetch(this.#routes().add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        await this.#removePartial(bundleId);
        this.#showMessage(data.description || data.message || this.dataset.errorText || 'Something went wrong.', 'error');
        return;
      }

      if (this.#giftOn()) {
        await this.#updateCart({
          attributes: {
            'Gift for': this.#giftValue('to'),
            'Gift note': this.#giftValue('msg'),
            'Gift from': this.#giftValue('from'),
          },
        });
      }

      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: chosen.map(({ option }) => ({ merchandiseId: String(option.variantId), quantity: 1 })),
          promise: deferred.promise,
        })
      );
      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ab-bundle-builder',
          sourceId: this.dataset.sectionId,
          itemCount: chosen.length,
          sections: data.sections,
          didError: false,
        },
      });
      this.#showMessage(this.dataset.addedText || 'Added to your bag.', 'success');
    } catch (error) {
      console.error('[ab-bundle-builder]', error);
      if (deferred) deferred.reject(error);
      this.#showMessage(this.dataset.errorText || 'Something went wrong.', 'error');
    } finally {
      this.#busy = false;
      for (const add of buttons) add.removeAttribute('aria-busy');
      this.#renderSummary();
    }
  }
}

if (!customElements.get('ab-bundle-builder')) customElements.define('ab-bundle-builder', AbBundleBuilder);
